import { Spinner } from '@app/components/icons/spinner';
import { CheckIcon } from '@app/features/analytics/icons/check';
import { CaretDown } from '@app/routes/content/routes/library/components/icons';
import { Combobox, Transition } from '@headlessui/react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, {
  Fragment,
  memo,
  Ref,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useInView } from 'react-intersection-observer';

import { useAnalytics } from '../../hooks/use-analytics';
import { useEventsFilter } from '../../hooks/use-events-filter';
import { SearchIcon } from '../../icons/search';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { EventType } from '../events/types';

export const EventFilter = () => {
  const comboboxButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('translation');
  const { track } = useAnalytics();
  const { state, dispatch: analyticsDispatch } = useAnalyticsContext();
  const selectedEvents = state.selectedEvents;

  const {
    filters,
    isEventsLoaded: hasEventsInitialized,
    eventsData,
    updateSearchText,
    fetchNextPage,
    fetchInitialEvents,
  } = useEventsFilter();

  const searchText = filters.searchText;
  const pageStatus = eventsData.status;
  const hasNextPage =
    eventsData.pagination.page < eventsData.pagination.totalPages;
  const events = eventsData.data;

  const { ref: loadingMoreRef, inView: loadingMoreInView } = useInView({
    rootMargin: '0px 0px 100px 0px',
    threshold: 0.1,
  });

  const { ref: noEventRef, inView: _noEventInView } = useInView({
    rootMargin: '0px 0px 100px 0px',
    threshold: 0.1,
  });

  // fetch next events if the next page is available
  useEffect(() => {
    if (pageStatus === 'processing') return;
    if (loadingMoreInView && hasNextPage) {
      fetchNextPage();
    }
  }, [loadingMoreInView, fetchNextPage, hasNextPage, pageStatus]);

  const handleSelect = useCallback(
    (eventExternalIds: string[]) => {
      track('ola_events_dropdown_check', {
        tab: state.selectedTab,
        numOfEventsChecked: eventExternalIds.length,
      });
      analyticsDispatch({
        type: 'update selected events',
        payload: { events: eventExternalIds },
      });
    },
    [analyticsDispatch, track, state.selectedTab],
  );

  const handleUnselectAll = useCallback(
    e => {
      e.preventDefault();
      analyticsDispatch({
        type: 'update selected events',
        payload: { events: [] },
      });
    },
    [analyticsDispatch],
  );

  return (
    <div className="h-10">
      <Combobox
        value={selectedEvents}
        onChange={handleSelect}
        multiple
        as="div"
        className="relative"
      >
        {({ open }) => (
          <>
            {/* Headless UI removes its button from Tab order because it expects
                a persistent input; our search input mounts only when open. */}
            <button
              ref={triggerRef}
              type="button"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={open ? 'analytics-event-options' : undefined}
              className={cx(
                'relative flex cursor-pointer border border-neutral-b4 rounded-lg h-10 px-2 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500',
                {
                  'ring-2 ring-blue-500': open,
                },
              )}
              onClick={() => comboboxButtonRef.current?.click()}
            >
              <span
                className={cx('text-sm flex items-center gap-1', {
                  'text-blue-500': open,
                  'text-black': !open,
                })}
              >
                {t('analytics.events')}
                {selectedEvents.length > 0 && (
                  <span className="text-xs flex items-center justify-center text-current bg-blue-500 text-white rounded-full px-2">
                    {selectedEvents.length}
                  </span>
                )}
              </span>
              <span className="pointer-events-none flex items-center pl-2">
                <CaretDown
                  width={16}
                  height={16}
                  className={`${open ? 'rotate-180' : ''}`}
                />
              </span>
            </button>
            <Combobox.Button
              ref={comboboxButtonRef}
              className="hidden"
              aria-hidden="true"
              onClick={() => {
                track('ola_events_dropdown_click', {
                  tab: state.selectedTab,
                });
                if (
                  !open &&
                  !hasEventsInitialized &&
                  pageStatus !== 'processing'
                ) {
                  fetchInitialEvents();
                }
              }}
            />

            {open && (
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute right-0 z-10 mt-1 w-80 overflow-hidden border border-neutral-b4 rounded-lg bg-white shadow-md outline-none">
                  <div className="pt-2">
                    <Combobox.Label className="block px-3 pb-1 text-xs font-medium text-neutral-b1">
                      {t('analytics.search-events')}
                    </Combobox.Label>
                    <div className="relative">
                      <span className="absolute left-3 top-3">
                        <SearchIcon />
                      </span>
                      <Combobox.Input
                        value={searchText || ''}
                        placeholder={t('analytics.search-events')}
                        onKeyDownCapture={event => {
                          if (event.key === 'Escape') {
                            requestAnimationFrame(() =>
                              triggerRef.current?.focus(),
                            );
                          }
                        }}
                        onChange={evt => {
                          track('ola_events_dropdown_search', {
                            tab: state.selectedTab,
                          });
                          updateSearchText(evt.target.value);
                        }}
                        className="border leading-none text-sm py-1.5 pl-9 pr-1 h-9 m-1 w-[calc(100%-8px)] rounded-lg"
                      />
                    </div>
                  </div>
                  <MemoisedLoader
                    id="initializing-loader"
                    show={!hasEventsInitialized}
                    label={t('analytics.loading')}
                  />
                  <UnselectAllButton
                    hasSelectedEvents={!!selectedEvents.length}
                    hasEventsInitialized={hasEventsInitialized}
                    onClick={handleUnselectAll}
                    label={t('analytics.unselect-all')}
                  />
                  <Combobox.Options
                    id="analytics-event-options"
                    aria-label={t('analytics.events')}
                    className="max-h-[300px] overflow-y-auto mt-1 mb-2"
                  >
                    {events.length === 0 && pageStatus !== 'processing' && (
                      <div
                        ref={noEventRef}
                        className="h-5 w-full flex flex-row justify-center items-center"
                      >
                        {t('analytics.no-events')}
                      </div>
                    )}
                    {events.map((event: EventType) => {
                      const displayName = event.name;
                      const eventValue = event.externalId;
                      const isSelected = selectedEvents.includes(eventValue);
                      return (
                        <Combobox.Option
                          key={event.slug}
                          value={eventValue}
                          className={cx(
                            'select-none px-3 py-2 flex items-center justify-between gap-1 cursor-pointer hover:bg-blue-100',
                            {
                              'bg-blue-100 text-blue-500': isSelected,
                              'bg-white text-black': !isSelected,
                            },
                          )}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {event.eventBannerUrl && (
                              <img
                                src={event.eventBannerUrl}
                                alt=""
                                className="w-8 h-8 object-cover rounded"
                              />
                            )}
                            <span className="truncate">{displayName}</span>
                          </div>
                          {isSelected && (
                            <span className="w-5 h-5 shrink-0">
                              <CheckIcon />
                            </span>
                          )}
                        </Combobox.Option>
                      );
                    })}
                    <MemoisedLoader
                      id="more-loader"
                      show={!!hasNextPage && pageStatus !== 'processing'}
                      _ref={loadingMoreRef}
                      label={t('analytics.loading')}
                    />
                  </Combobox.Options>
                </div>
              </Transition>
            )}
          </>
        )}
      </Combobox>
    </div>
  );
};

const UnselectAllButton = ({
  hasSelectedEvents,
  hasEventsInitialized,
  onClick,
  label,
}: {
  hasSelectedEvents: boolean;
  hasEventsInitialized: boolean;
  onClick: (e: any) => void;
  label: string;
}) => {
  if (!hasSelectedEvents || !hasEventsInitialized) return null;

  return (
    <div className="h-6 w-full flex flex-row justify-end pr-1">
      <Combobox.Button
        className="relative h-6 py-1 px-2 border text-xs border-neutral-b4 rounded-lg shadow-sm outline-none"
        onClick={onClick}
      >
        {label}
      </Combobox.Button>
    </div>
  );
};

const MemoisedLoader = memo(function Loader({
  id,
  show,
  _ref,
  label,
}: {
  id: string;
  show: boolean;
  _ref?: Ref<HTMLDivElement>;
  label: string;
}) {
  if (!show) return null;

  return (
    <div
      id={id}
      ref={_ref}
      className="h-5 w-full flex flex-row justify-center items-center"
    >
      <Spinner className="my-2" /> {label}
    </div>
  );
});
